import type {MouseEvent, ReactNode} from "react";
import {useState} from "react";

import {Badge, badgeVariants} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardAction, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Loader2, Map, RefreshCw, Users} from "lucide-react";
import {cn} from "@/lib/utils";
import {BadgeOverflowList, Loader} from "@/src/modules/core/components";
import {formatUsPhoneDisplay} from "@/src/modules/properties/lib/us-phone";
import {CoverageMap} from "@/src/modules/shifts/components/coverage";
import {getCoverageShiftTypeCardClass} from "@/src/modules/shifts/lib/coverage-shift-type-styles";
import {ShiftCoverageProperty, ShiftCoverageRow} from "@/src/modules/shifts/types";

import {getCoverageBreakStatus} from "../lib/coverage-break-status";
import {formatShiftWindow} from "../lib/format-shift-window";
import {
  DISPATCH_DESK_COVERAGE_MAP_DIALOG_HEIGHT_CLASS,
  DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS,
  DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
  DISPATCH_DESK_PANEL_SCROLL_CLASS,
} from "../lib/dispatch-desk-layout";
import {
  DISPATCH_DESK_BLOCK_HEADER_CLASS,
  DISPATCH_DESK_BLOCK_TITLE_CLASS,
  DISPATCH_DESK_CARD_CLASS,
  DISPATCH_DESK_HEADER_ICON_ACTION_CLASS,
  DISPATCH_DESK_HEADER_TEXT_ACTION_CLASS,
  DISPATCH_DESK_PANEL_SHELL_CLASS,
  DispatchDeskHeaderIconRow,
  DISPATCH_DESK_CONTENT_MUTED_CLASS,
  DISPATCH_DESK_ROW_PRIMARY_CLASS,
  DISPATCH_DESK_ROW_SECONDARY_CLASS,
} from "./dispatch-desk-card";

type BreakBadgeVariant = NonNullable<Parameters<typeof badgeVariants>[0]>["variant"];

/** Distinct from the role badge's `outline` variant so the two chips never look identical. */
const BREAK_TONE_VARIANT: Record<string, BreakBadgeVariant> = {
  warning: "warning",
  destructive: "destructive",
  muted: "secondary",
};

function OnDutyMetaRow({label, children}: {label: string; children: ReactNode}) {
  return (
    <div className="grid grid-cols-[4.25rem_minmax(0,1fr)] items-baseline gap-x-2">
      <span className={cn("shrink-0", DISPATCH_DESK_ROW_PRIMARY_CLASS)}>{label}</span>
      <div className={cn("min-w-0", DISPATCH_DESK_ROW_SECONDARY_CLASS)}>{children}</div>
    </div>
  );
}

function BreakChip({status}: {status: ReturnType<typeof getCoverageBreakStatus>}) {
  if (status.kind === "none") return null;

  return (
    <Badge variant={BREAK_TONE_VARIANT[status.tone]} className="shrink-0">
      {status.label}
    </Badge>
  );
}

type OnDutyRowProps = {
  row: ShiftCoverageRow;
  isSelected: boolean;
  onSelect: () => void;
};

function OnDutyRow({row, isSelected, onSelect}: OnDutyRowProps) {
  const officerPhone = row.telephone?.trim();
  const assignedProperties = [...row.assigned.properties].sort(
    (a, b) => a.sort_order - b.sort_order,
  );
  const roleName = row.role?.name?.trim();
  const breakStatus = getCoverageBreakStatus(row.breaks);
  const hasRole = Boolean(roleName);
  const hasBreak = breakStatus.kind !== "none";
  const hasPhone = Boolean(officerPhone);
  const useBadgeGrid = hasRole || hasBreak;

  const stopLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  const phoneRow = hasPhone ? (
    <OnDutyMetaRow label="Phone">
      <a
        href={`tel:${officerPhone!.replace(/\D/g, "")}`}
        className="tabular-nums hover:text-foreground hover:underline"
        onClick={stopLinkClick}
      >
        {formatUsPhoneDisplay(row.telephone)}
      </a>
    </OnDutyMetaRow>
  ) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-selected={isSelected}
      className={cn(
        "relative border-0 px-2 py-3 text-left",
        getCoverageShiftTypeCardClass(row.shift_type, isSelected),
      )}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="flex flex-col gap-2">
        {useBadgeGrid ? (
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-1">
            <p
              className={cn(
                "col-start-1 row-start-1 min-w-0 self-start font-normal leading-snug",
                DISPATCH_DESK_ROW_PRIMARY_CLASS,
                isSelected && "font-semibold",
              )}
            >
              {row.officer_name}
            </p>
            {hasRole ? (
              <Badge
                variant="outline"
                className="col-start-2 row-start-1 shrink-0 justify-self-end capitalize"
              >
                {roleName}
              </Badge>
            ) : null}
            {hasPhone ? (
              <div className="col-start-1 row-start-2 min-w-0">{phoneRow}</div>
            ) : null}
            {hasBreak ? (
              <div
                className={cn(
                  "col-start-2 shrink-0 justify-self-end self-start",
                  hasPhone || hasRole ? "row-start-2" : "row-start-1",
                )}
              >
                <BreakChip status={breakStatus} />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <p
              className={cn(
                "min-w-0 font-normal leading-snug",
                DISPATCH_DESK_ROW_PRIMARY_CLASS,
                isSelected && "font-semibold",
              )}
            >
              {row.officer_name}
            </p>
            {phoneRow}
          </div>
        )}

        <OnDutyMetaRow label="Properties">
          {assignedProperties.length > 0 ? (
            <BadgeOverflowList
              align="start"
              className="min-w-0"
              overflowPairLayout="flex"
              items={assignedProperties}
              getKey={(property) =>
                `${row.shift_id}-${property.property_id}-${property.sort_order}`
              }
              getLabel={(property) => property.name}
              popoverTitle="All Properties"
              popoverContentClassName="w-72"
              listClassName="flex flex-col gap-1.5"
              visibleBadgeVariant="secondary"
              visibleBadgeClassName="font-normal"
              moreTriggerClassName="font-normal"
              renderPopoverItem={(property: ShiftCoverageProperty) => (
                <p className={cn("flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0", DISPATCH_DESK_ROW_PRIMARY_CLASS)}>
                  <span>{property.name}</span>
                  <span className={cn("tabular-nums", DISPATCH_DESK_ROW_SECONDARY_CLASS)}>
                    {formatUsPhoneDisplay(property.site_telephone)}
                  </span>
                </p>
              )}
            />
          ) : (
            <span className="text-foreground">-</span>
          )}
        </OnDutyMetaRow>

        <OnDutyMetaRow label="Shift">
          <span className="tabular-nums">{formatShiftWindow(row.from_datetime, row.to_datetime)}</span>
        </OnDutyMetaRow>
      </div>
    </div>
  );
}

type DispatchOnDutyListProps = {
  rows: ShiftCoverageRow[];
  selectedShiftId: number | null;
  onSelectShift: (shiftId: number | null) => void;
  isPending: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  className?: string;
};

export function DispatchOnDutyList({
  rows,
  selectedShiftId,
  onSelectShift,
  isPending,
  isFetching,
  onRefresh,
  className,
}: DispatchOnDutyListProps) {
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <>
      <Card
        className={cn(
          DISPATCH_DESK_CARD_CLASS,
          DISPATCH_DESK_PANEL_SHELL_CLASS,
          DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
          className,
        )}
      >
        <CardHeader className={DISPATCH_DESK_BLOCK_HEADER_CLASS}>
          <DispatchDeskHeaderIconRow icon={Users} className="min-w-0">
            <CardTitle className={DISPATCH_DESK_BLOCK_TITLE_CLASS}>On duty</CardTitle>
            {rows.length > 0 ? (
              <span className={DISPATCH_DESK_CONTENT_MUTED_CLASS}>
                · {rows.length} officer{rows.length === 1 ? "" : "s"}
              </span>
            ) : null}
          </DispatchDeskHeaderIconRow>
          <CardAction className="row-span-1 self-start">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "text-muted-foreground hover:text-foreground",
                  DISPATCH_DESK_HEADER_ICON_ACTION_CLASS,
                )}
                disabled={isFetching}
                aria-label="Refresh on duty roster"
                onClick={onRefresh}
              >
                {isFetching ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="size-3.5" aria-hidden />
                )}
              </Button>
              {rows.length > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={DISPATCH_DESK_HEADER_TEXT_ACTION_CLASS}
                  onClick={() => setMapOpen(true)}
                >
                  <Map className="size-3.5" aria-hidden />
                  Show map
                </Button>
              ) : null}
            </div>
          </CardAction>
        </CardHeader>
        <CardContent className={cn(DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS, "gap-3")}>
          {isPending ? (
            <Loader className="h-40" />
          ) : (
            <div className={cn(DISPATCH_DESK_PANEL_SCROLL_CLASS, "pr-1")}>
              <div className="divide-border flex flex-col divide-y">
                {rows.map((row) => {
                  const isSelected = row.shift_id === selectedShiftId;
                  return (
                    <OnDutyRow
                      key={row.shift_id}
                      row={row}
                      isSelected={isSelected}
                      onSelect={() => onSelectShift(isSelected ? null : row.shift_id)}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {isFetching && !isPending ? (
            <p className={cn("shrink-0", DISPATCH_DESK_CONTENT_MUTED_CLASS)}>Refreshing…</p>
          ) : null}
        </CardContent>
      </Card>

      {rows.length > 0 ? (
        <Dialog open={mapOpen} onOpenChange={setMapOpen}>
          <DialogContent className="gap-4 sm:max-w-[min(96vw,90rem)]">
            <DialogHeader>
              <DialogTitle>Coverage map</DialogTitle>
            </DialogHeader>
            <CoverageMap
              rows={rows}
              selectedShiftId={selectedShiftId}
              onSelectShift={onSelectShift}
              hideTitle
              toolbarButtonClassName={DISPATCH_DESK_HEADER_TEXT_ACTION_CLASS}
              mapHeightClass={DISPATCH_DESK_COVERAGE_MAP_DIALOG_HEIGHT_CLASS}
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
